/**
 * Generic time-series aggregation utilities
 *
 * Provides type-safe functions for:
 * - Aggregating data by time periods (day/week/month/quarter)
 * - Normalizing values to 0-100 index scale
 * - Calculating percentage changes
 */

import { DateTimeSDK } from './dates';

// ============================================================================
// Types
// ============================================================================

/** Base type for items that can be aggregated by time */
export type DateItem = {
    date: string; // ISO date string (YYYY-MM-DD)
};

/** Base aggregated result with label and key */
export type AggregatedBase = {
    label: string;
    key: string;
};

/** Aggregation method for numeric values */
export type AggregationMethod = 'sum' | 'avg' | 'max' | 'min' | 'first' | 'last';

/** Field configuration for aggregation */
export type FieldConfig<T> = {
    /** How to extract the value from source data */
    getValue: (item: T) => number;
    /** How to aggregate multiple values (default: 'sum') */
    method?: AggregationMethod;
};

/** Result type with dynamic fields based on field names */
export type AggregatedResult<F extends string> = AggregatedBase & {
    [K in F]: number;
};

/** Result type with index fields */
export type IndexedResult<F extends string> = AggregatedBase & {
    [K in F]: number;
} & {
    [K in F as `${K}Index`]: number;
};

// ============================================================================
// Field Helpers
// ============================================================================

/** Helper functions for common field configurations */
export const FieldHelpers = {
    /** Direct field access */
    direct: <T, K extends keyof T>(field: K): FieldConfig<T> => ({
        getValue: (item: T) => Number(item[field]) || 0,
    }),

    /** Computed value with fallback to 0 */
    computed: <T>(fn: (item: T) => number): FieldConfig<T> => ({
        getValue: fn,
    }),

    /** Field with custom fallback */
    withFallback: <T>(fn: (item: T) => number | undefined | null): FieldConfig<T> => ({
        getValue: (item: T) => Number(fn(item)) || 0,
    }),
};

// ============================================================================
// Aggregation Functions
// ============================================================================

/** Aggregate values based on method */
function aggregateValues(values: number[], method: AggregationMethod): number {
    if (values.length === 0) return 0;

    // Ensure all values are numbers to prevent string concatenation
    const numericValues = values.map(Number);

    switch (method) {
        case 'sum':
            return numericValues.reduce((sum, v) => sum + v, 0);
        case 'avg':
            return numericValues.reduce((sum, v) => sum + v, 0) / numericValues.length;
        case 'max':
            return Math.max(...numericValues);
        case 'min':
            return Math.min(...numericValues);
        case 'first':
            return numericValues[0]!;
        case 'last':
            return numericValues[numericValues.length - 1]!;
    }
}

/**
 * Generic time-series aggregation function
 *
 * @param data - Array of items with a date field
 * @param groupBy - How to group the data (day/week/month/quarter)
 * @param fields - Configuration for each output field
 * @returns Array of aggregated results with labels
 */
export function aggregateByTime<T extends DateItem, F extends string>(
    data: T[],
    groupBy: DateTimeSDK.GroupBy,
    fields: Record<F, FieldConfig<T>>,
): AggregatedResult<F>[] {
    // Group data by time period
    const groups = new Map<string, T[]>();

    for (const item of data) {
        // Parse ISO date string to Date object
        const dateObj = new Date(item.date);
        const key = DateTimeSDK.getGroupKey(dateObj, groupBy);
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)!.push(item);
    }

    // Sort keys chronologically
    const sortedKeys = Array.from(groups.keys()).sort();

    // Aggregate each group
    return sortedKeys.map(key => {
        const items = groups.get(key)!;
        const label = DateTimeSDK.formatGroupLabel(key, groupBy);

        const result: Record<string, unknown> = { label, key };

        for (const [fieldName, config] of Object.entries(fields) as [F, FieldConfig<T>][]) {
            const values = items.map(item => config.getValue(item));
            result[fieldName] = aggregateValues(values, config.method ?? 'sum');
        }

        return result as AggregatedResult<F>;
    });
}

/**
 * Normalize aggregated stats to 0-100 index scale
 * Each field is normalized independently (its max value becomes 100)
 *
 * @param stats - Array of aggregated stats
 * @param fields - Fields to normalize
 * @returns Array with original values plus index fields
 */
export function normalizeToIndex<T extends AggregatedBase, F extends string>(
    stats: T[],
    fields: readonly F[],
): IndexedResult<F>[] {
    if (stats.length === 0) return [];

    // Find max value for each field
    const maxValues: Record<string, number> = {};
    for (const field of fields) {
        maxValues[field] = Math.max(
            ...stats.map(s => Number((s as Record<string, unknown>)[field]) || 0),
        );
    }

    // Normalize each stat
    return stats.map(stat => {
        const result = { ...stat } as Record<string, unknown>;

        for (const field of fields) {
            const value = Number((stat as Record<string, unknown>)[field]) || 0;
            const max = maxValues[field] || 1;
            result[`${field}Index`] = max > 0 ? Math.round((value / max) * 100) : 0;
        }

        return result as IndexedResult<F>;
    });
}

/**
 * Calculate percentage change between two values
 * Returns null if previous is 0 and current is also 0
 */
export function calculateChange(current: number, previous: number): number | null {
    if (previous === 0) {
        return current > 0 ? 100 : null;
    }
    return Math.round(((current - previous) / previous) * 100);
}
